// Plain-language labels for non-technical operators. Registry-first since the
// CS shell (spec §4.2): when the caller has the transform or connector list from
// the API, the label declared next to the connector code wins. The local maps
// are the offline fallback — complete for every transform this repo registers —
// and the raw id is the last resort so nothing ever renders blank.

export interface TransformLabelSource {
  id: string;
  label?: string | null;
}

const TRANSFORM_DESCRIPTIONS: Record<string, string> = {
  "jump.fan.profile→hubspot.contact": "Fan profiles → HubSpot contacts",
  "hubspot.contact→jump.fan": "HubSpot contacts → Fan profiles",
  "hubspot.contact→jump.profile": "HubSpot contacts → Fan profiles (profile service)",
  "jump.fan.order→hubspot.deal": "Orders → HubSpot deals",
  "jump.order.item→hubspot.line_item": "Order items → HubSpot line items",
  "jump.form_submission→custom-sqs": "Form submissions → your queue",
  "jump.fan→custom-sqs": "Fan profiles → your queue",
  "jump.order→custom-sqs": "Orders → your queue",
  "jump.order_item→custom-sqs": "Order items → your queue",
};

/**
 * What a pipeline moves, in plain words: "Fan profiles → HubSpot contacts".
 *
 * Registry first when the caller has the transform list, because the label
 * declared next to the connector code is the one that stays current. The local
 * map is the offline fallback, and the raw id is the last resort — reaching it
 * means a transform shipped without a label, not that an id belongs on screen.
 */
export function transformDescription(
  transformId: string | undefined | null,
  transforms?: readonly TransformLabelSource[],
): string {
  if (!transformId) return "—";
  const fromRegistry = transforms?.find((t) => t.id === transformId)?.label;
  if (fromRegistry) return fromRegistry;
  return TRANSFORM_DESCRIPTIONS[transformId] ?? transformId;
}

export interface VendorLabelSource {
  connectorType: string;
  label?: string | null;
}

// Friendly vendor name for a connector type, so the data flow reads
// "Jump → HubSpot" instead of "jump-source → hubspot".
const VENDOR_LABELS: Record<string, string> = {
  "jump-source": "Jump",
  "jump-dest": "Jump",
  "jump-profile-dest": "Jump",
  hubspot: "HubSpot",
  snowflake: "Snowflake",
  "snowflake-source": "Snowflake",
  "custom-sqs": "Your queue",
};

/**
 * A connector type as a vendor name, so a flow reads "Jump → HubSpot" rather
 * than "jump-source → hubspot". Resolves registry first, local map second.
 */
export function vendorLabel(
  connectorType: string | undefined | null,
  connectors?: readonly VendorLabelSource[],
): string {
  if (!connectorType) return "—";
  const fromRegistry = connectors?.find((c) => c.connectorType === connectorType)?.label;
  if (fromRegistry) return fromRegistry;
  return VENDOR_LABELS[connectorType] ?? connectorType;
}
